<template>
  <div class="override-info-panel">
    <div class="panel-header">
      <i class="fa-solid fa-layer-group mr-2" />
      <span class="font-semibold">{{ t('segmentation.override.panelTitle') }}</span>
    </div>

    <div v-if="loading" class="panel-content">
      <Skeleton height="1rem" class="mb-2" />
      <Skeleton height="1rem" class="mb-2" />
      <Skeleton height="1rem" />
    </div>

    <div v-else-if="error" class="panel-content">
      <div class="error-message">
        <i class="fa-solid fa-triangle-exclamation mr-2" />
        <span>{{ t('segmentation.override.errorLoading') }}</span>
      </div>
    </div>

    <div v-else class="panel-content">
      <div class="info-message">
        <i class="fa-solid fa-circle-info mr-2" />
        <p>{{ t('segmentation.override.infoMessage') }}</p>
      </div>

      <div class="overrides-list">
        <div class="list-title">
          <i class="fa-solid fa-list-ol mr-2" />
          <span>{{ t('segmentation.override.orderTitle') }}</span>
        </div>

        <div
          v-for="(override, index) in overrides"
          :key="override.id_custom_data_definition"
          class="override-item"
        >
          <div class="override-priority">{{ index + 1 }}</div>
          <div class="override-content">
            <span class="schema-name">{{ override.schemaName }}</span>
            <span class="separator">•</span>
            <span class="field-badge">{{ override.custom_field_name }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import Skeleton from 'primevue/skeleton';
import useProductOverrides from '@/composables/segmentations/useProductOverrides';

interface Override {
  id_custom_data_definition: number;
  custom_field_name: string;
  system_field_name: string;
  priority: number;
  schemaName?: string;
}

export default defineComponent({
  name: 'OverrideInfoPanel',

  components: {
    Skeleton,
  },

  props: {
    systemFieldName: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const { getOverridesForField, isLoading, hasError } = useProductOverrides();

    const overrides = computed(() => getOverridesForField(props.systemFieldName));
    const loading = computed(() => isLoading.value);
    const error = computed(() => hasError.value);

    return {
      t,
      overrides,
      loading,
      error,
    };
  },
});
</script>

<style lang="scss" scoped>
.override-info-panel {
  min-width: 400px;
  max-width: 500px;
}

.panel-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  border-radius: 6px 6px 0 0;
  font-size: 0.95rem;
}

.panel-content {
  padding: 1rem;
}

.info-message {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  background-color: #eff6ff;
  border-left: 3px solid #3b82f6;
  border-radius: 4px;
  margin-bottom: 1rem;

  i {
    color: #3b82f6;
    margin-top: 0.1rem;
    flex-shrink: 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #1e40af;
  }
}

.error-message {
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background-color: #fef2f2;
  border-left: 3px solid #ef4444;
  border-radius: 4px;
  color: #991b1b;
  font-size: 0.875rem;

  i {
    color: #ef4444;
  }
}

.overrides-list {
  .list-title {
    display: flex;
    align-items: center;
    font-weight: 600;
    font-size: 0.875rem;
    color: #374151;
    margin-bottom: 0.75rem;

    i {
      color: #f59e0b;
    }
  }
}

.override-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
  background-color: #fffbeb;
  border: 2px solid #fed7aa;
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #fef3c7;
    border-color: #fdba74;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .override-priority {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
    color: white;
    border-radius: 50%;
    font-weight: 700;
    font-size: 1.125rem;
    flex-shrink: 0;
    margin-right: 1.25rem;
    box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
  }

  .override-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
    min-width: 0;

    .schema-name {
      font-weight: 500;
      color: #4b5563;
      font-size: 1rem;
    }

    .separator {
      color: #d1d5db;
      font-weight: 400;
      font-size: 1.25rem;
      line-height: 1;
    }

    .field-badge {
      background-color: #dbeafe;
      color: #3b82f6;
      font-weight: 500;
      font-size: 0.875rem;
      padding: 0.25rem 0.75rem;
      border-radius: 6px;
      font-family: 'Courier New', Courier, monospace;
    }
  }
}
</style>
